/**
 * 2D outline(s) → prism mesh (positions/normals/indices as plain typed arrays — NO
 * three.js in this package; the app wraps buffers in BufferGeometry). World axes: X
 * east, Y north (plan), Z up. Vertices are duplicated per face for hard edges (flat
 * shading, no smoothing) — EXCEPT the facets a caller identifies as belonging to a
 * true arc (CURVES round: an optional `circles` list passed to `extrudeOutlines`),
 * whose side faces get the ANALYTIC radial normal at each vertex instead of their own
 * flat facet normal — a curved wall/slab edge reads as a smooth cylinder, not a
 * faceted polygon, while every straight edge and every real corner/miter keeps its
 * hard edge exactly as before (classifyRingArcRuns is the one arc-run detector this
 * shares with the 2D true-arc linework channel — see arcs2d.ts).
 *
 * Outer/hole classification is by CONTAINMENT, not winding sign — Clipper2's orientation
 * convention is Y-down, so sign alone flips meaning in our Y-up world (a real bug caught
 * by the first test run; do not "simplify" this back to a sign check).
 */
import earcut from 'earcut'
import type { Pt2 } from '@aj/bim-kernel'
import { signedArea } from './clipper'
import { classifyRingArcRuns, type ArcCircle } from './arcs2d'

export interface MeshBuffers {
  positions: Float32Array
  normals: Float32Array
  indices: Uint32Array
}

export class MeshBuilder {
  pos: number[] = []
  nrm: number[] = []
  idx: number[] = []
  vertCount = 0
  /** three DISTINCT per-vertex normals — the smooth-shading path (arc-derived side
   *  faces). `addTri` (below) is the flat-shaded convenience: na=nb=nc=n. */
  addTriN(a: number[], b: number[], c: number[], na: number[], nb: number[], nc: number[]): void {
    this.pos.push(...a, ...b, ...c)
    this.nrm.push(...na, ...nb, ...nc)
    this.idx.push(this.vertCount, this.vertCount + 1, this.vertCount + 2)
    this.vertCount += 3
  }
  addTri(a: number[], b: number[], c: number[], n: number[]): void {
    this.addTriN(a, b, c, n, n, n)
  }
  build(): MeshBuffers {
    return {
      positions: new Float32Array(this.pos),
      normals: new Float32Array(this.nrm),
      indices: new Uint32Array(this.idx),
    }
  }
}

function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export interface PolyWithHoles { outer: Pt2[]; holes: Pt2[][] }

/** Classify rings into outers + their holes by containment (one nesting level). */
export function ringsToPolys(rings: Pt2[][]): PolyWithHoles[] {
  const real = rings.filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > 1e-9)
  const sorted = [...real].sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)))
  const polys: PolyWithHoles[] = []
  for (const ring of sorted) {
    const host = polys.find((p) => pointInRing(ring[0], p.outer))
    if (host) host.holes.push(ring)
    else polys.push({ outer: ring, holes: [] })
  }
  return polys
}

const asCCW = (ring: Pt2[]): Pt2[] => (signedArea(ring) > 0 ? ring : [...ring].reverse())
const asCW = (ring: Pt2[]): Pt2[] => (signedArea(ring) < 0 ? ring : [...ring].reverse())

/** The analytic smooth normal at `pt` for a vertex on `circle` — the radial direction,
 *  sign-matched to `fallback` (the facet's own flat normal) so it points the same
 *  general way (outward vs. into a cavity) without re-deriving that convention. */
function radialNormalAt(pt: Pt2, circle: ArcCircle, fallback: number[]): number[] {
  const dx = pt[0] - circle.c[0], dy = pt[1] - circle.c[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return fallback
  let rx = dx / len, ry = dy / len
  if (rx * fallback[0] + ry * fallback[1] < 0) { rx = -rx; ry = -ry }
  return [rx, ry, 0]
}

function addSides(b: MeshBuilder, ring: Pt2[], zBase: number, zTop: number, circles?: ArcCircle[]): void {
  const n = ring.length
  // CURVES round: classify once per ring — which edges are genuine ≥2-facet runs on
  // a known circle (vs. real corners/miters/straight runs, which stay flat/hard)
  const runOf = circles?.length ? classifyRingArcRuns(ring, circles) : null
  for (let i = 0; i < n; i++) {
    const p = ring[i], q = ring[(i + 1) % n]
    const dx = q[0] - p[0], dy = q[1] - p[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) continue
    // right normal of travel: outward for a CCW outer, into the cavity for a CW hole
    const nx = dy / len, ny = -dx / len
    const a0 = [p[0], p[1], zBase], a1 = [q[0], q[1], zBase]
    const a2 = [q[0], q[1], zTop], a3 = [p[0], p[1], zTop]
    const norm = [nx, ny, 0]
    const k = runOf ? runOf[i] : -1
    if (k >= 0) {
      const np = radialNormalAt(p, circles![k], norm)
      const nq = radialNormalAt(q, circles![k], norm)
      b.addTriN(a0, a1, a2, np, nq, nq)
      b.addTriN(a0, a2, a3, np, nq, np)
    } else {
      b.addTri(a0, a1, a2, norm)
      b.addTri(a0, a2, a3, norm)
    }
  }
}

function addCaps(b: MeshBuilder, poly: PolyWithHoles, zBase: number, zTop: number, skip?: { top?: boolean; bottom?: boolean }): void {
  const outer = asCCW(poly.outer)
  const holes = poly.holes.map(asCW)
  const flat: number[] = []
  const all: Pt2[] = []
  for (const [x, y] of outer) { flat.push(x, y); all.push([x, y]) }
  const holeIdx: number[] = []
  for (const h of holes) {
    holeIdx.push(all.length)
    for (const [x, y] of h) { flat.push(x, y); all.push([x, y]) }
  }
  const tris = earcut(flat, holeIdx.length ? holeIdx : undefined)
  for (let t = 0; t < tris.length; t += 3) {
    const [i, j, k] = [tris[t], tris[t + 1], tris[t + 2]]
    const P = (m: number, z: number) => [all[m][0], all[m][1], z]
    if (!skip?.top) b.addTri(P(i, zTop), P(j, zTop), P(k, zTop), [0, 0, 1])
    if (!skip?.bottom) b.addTri(P(k, zBase), P(j, zBase), P(i, zBase), [0, 0, -1])
  }
}

/** Extrude one simple closed outline (no holes) from zBase to zTop. `circles` (CURVES
 *  round) — candidate source circles whose facet runs should smooth-shade. */
export function extrudePrism(outline: Pt2[], zBase: number, zTop: number, circles?: ArcCircle[]): MeshBuffers | null {
  return extrudeOutlines([outline], zBase, zTop, circles)
}

/** Extrude a ring set (outers + holes, any winding/order) into one prism mesh. */
/** `skipCaps` (ELEVATION CLEANUP round, 2026-10-19): leave off a cap a derived mesh
 *  continues past (a wall's roof-attached top, stepped base): that cap is buried inside
 *  the element, and the hidden-line pass would read it as a real face and draw the joint */
export function extrudeOutlines(rings: Pt2[][], zBase: number, zTop: number, circles?: ArcCircle[], skipCaps?: { top?: boolean; bottom?: boolean }): MeshBuffers | null {
  if (zTop <= zBase) return null
  const polys = ringsToPolys(rings)
  if (polys.length === 0) return null
  const b = new MeshBuilder()
  for (const poly of polys) {
    addSides(b, asCCW(poly.outer), zBase, zTop, circles)
    for (const h of poly.holes) addSides(b, asCW(h), zBase, zTop, circles)
    addCaps(b, poly, zBase, zTop, skipCaps)
  }
  return b.vertCount > 0 ? b.build() : null
}
